import { Carousel } from '@elipssolution/harfang';
import { useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';

import SimulatorItem from './components/SimulatorItem';
import { SimulatorDetailsType } from './types/simulator';
import { WidgetSimulatorsContext } from './utils/context';
import { getSimulatorDetails } from './utils/simulatorDetailsUtil';
import useFormat from '../../../hooks/useFormat';
import { WidgetComponentProps, WidgetSimulatorsConfigurationType } from '../../../types/widget';
import { measureTextWidth } from '../../../utils/measureTextWidth';
import { RenderModeEnum } from '../../../utils/widgets';
import { useSession } from '../../components/session/SessionContext';
import useCustomRouter from '../../hooks/useCustomRouter';
import { UserTypeEnum } from '../../types/user';

const widgetContextValue = { renderMode: RenderModeEnum.CAROUSEL };

const WidgetSimulator = ({ configuration }: WidgetComponentProps<WidgetSimulatorsConfigurationType>) => {
	const { query } = useCustomRouter();
	const { isMobileFormat } = useFormat();
	const { typography } = useTheme();
	const {
		user: { type: userType },
	} = useSession();

	const isInSettingsDialog = query.settings;

	const itemWidth = isMobileFormat ? 70 : 132;

	const carouselItems = useMemo(
		() =>
			(configuration?.simulators ?? [])
				.sort((a, b) => a.name.localeCompare(b.name))
				.reduce(
					(acc, simulator) =>
						(userType === UserTypeEnum.INTERNAL && !simulator.isInternal) ||
						(userType === UserTypeEnum.EXTERNAL && !simulator.isExternal) ||
						(isMobileFormat && !simulator.isAvailableOnMobileFormat) ||
						(!isMobileFormat && !simulator.isAvailableOnDesktopFormat)
							? acc
							: ([...acc, getSimulatorDetails({ simulator })] as SimulatorDetailsType[]),
					[] as SimulatorDetailsType[],
				),
		[configuration, isMobileFormat, userType],
	);

	const hasSimulatorNamesOnTwoLines = useMemo(
		() =>
			(configuration?.simulators || []).some(
				({ name }) =>
					measureTextWidth({
						text: name,
						fontSize: typography.body1.fontSize as string,
						fontFamily: typography.body1.fontFamily as string,
					}) >=
					itemWidth - 8 - 8,
			),
		[configuration?.simulators, itemWidth, typography.body1.fontFamily, typography.body1.fontSize],
	);

	const itemHeight = useMemo(() => {
		if (!isMobileFormat) {
			return 132;
		}

		return hasSimulatorNamesOnTwoLines ? 120 : 96;
	}, [hasSimulatorNamesOnTwoLines, isMobileFormat]);

	const dataSource = useCallback(
		() =>
			Promise.resolve({
				items: carouselItems,
				count: carouselItems.length,
			}),
		[carouselItems],
	);

	const renderItem = ({ backgroundColor, icon, name }: SimulatorDetailsType) => (
		<SimulatorItem name={name} backgroundColor={backgroundColor} icon={icon} />
	);

	const handleItemClick = ({ url }: SimulatorDetailsType) => !isInSettingsDialog && window.open(url, '_blank');

	return (
		<WidgetSimulatorsContext.Provider value={widgetContextValue}>
			<Carousel
				dataSource={dataSource}
				itemWidth={itemWidth}
				itemHeight={itemHeight}
				renderItem={renderItem}
				onItemClick={handleItemClick}
			/>
		</WidgetSimulatorsContext.Provider>
	);
};

export default WidgetSimulator;
